import { useCallback, useState, useRef, useEffect } from 'react'
import { useAppDispatch, useIsGeorgetownSite } from '../hooks'
import { setSearchDescription } from './searchSlice'
import * as API from './api'
import { setBookings } from '../bookings/bookingsSlice'

export const useSearchForm = () => {
  const dispatch = useAppDispatch()
  const lastNameRef = useRef<HTMLInputElement>(null)
  const bookingDateRef = useRef<HTMLInputElement>(null)
  const releaseDateRef = useRef<HTMLInputElement>(null)
  const isGeorgetown = useIsGeorgetownSite()

  const [isSearching, setIsSearching] = useState(false)

  const search = useCallback(() => {
    const lastName = lastNameRef.current?.value.trim() || ''
    const bookingDate = bookingDateRef.current?.value.trim() || ''
    const releaseDate = releaseDateRef.current?.value.trim() || ''
    
    if (!isSearching) {
      if (!lastName && !bookingDate && !releaseDate) return
      setIsSearching(true)
      const runSearch = async () => {
        try {
          const bookings = await API.search(lastName, bookingDate, releaseDate, isGeorgetown ? 'georgetown' : 'horry')
          let searchTerms = []
          if (lastName) searchTerms.push(`Last Name: ${lastName}`)
          if (bookingDate) searchTerms.push(`Booking Date: ${bookingDate}`)
          if (releaseDate) searchTerms.push(`Release Date: ${releaseDate}`)
          dispatch(setSearchDescription(`for ${searchTerms.join(', ')}`))
          dispatch(setBookings(bookings))
        } finally {
          setIsSearching(false)
        }
      }
      runSearch()
    }
  }, [lastNameRef, bookingDateRef, releaseDateRef, isSearching, isGeorgetown, setIsSearching, dispatch])

  // Initially load the bookings for today
  useEffect(() => {
    if (!dispatch || !setIsSearching) return
    const initialSearch = async () => {
      setIsSearching(true)
      try {
        const today = (new Date()).toLocaleDateString()
        const bookings = await API.search('', today, '', isGeorgetown ? 'georgetown' : 'horry')
        dispatch(setSearchDescription(`for Booking Date: ${today}`))
        dispatch(setBookings(bookings))
      } finally {
        setIsSearching(false)
      }
    }
    initialSearch()
  }, [dispatch, isGeorgetown, setIsSearching])

  const clear = useCallback(() => {
    if (lastNameRef.current) lastNameRef.current.value = ''
    if (bookingDateRef.current) bookingDateRef.current.value = ''
    if (releaseDateRef.current) releaseDateRef.current.value = ''
  }, [lastNameRef, bookingDateRef, releaseDateRef])

  return { 
    isSearching, 
    search, 
    clear, 
    lastNameRef, 
    bookingDateRef, 
    releaseDateRef, 
  }
}